import * as models from 'app/models';

// Shared helpers for translating between a zone's NORMALIZED (0-1) shape
// coordinates (what we persist in ZoneMapData) and the floor-plan image's local
// pixel space (what Konva / SVG draw in). Keeping this pure lets both the Konva
// editor and the read-only SVG viewer share exactly the same math.

export const DEFAULT_ZONE_COLOR = '#2f9e54';

export interface Box { x: number; y: number; width: number; height: number; }

// Convert normalized coords -> image-local pixel coords for the given image size.
export function toLocalPoints(points: number[], iw: number, ih: number): number[] {
    let out: number[] = [];
    for (let i = 0; i < points.length; i += 2) {
        out.push(points[i] * iw, points[i + 1] * ih);
    }
    return out;
}

// Convert image-local pixel coords -> normalized (clamped to 0-1).
export function toNormalizedPoints(points: number[], iw: number, ih: number): number[] {
    let out: number[] = [];
    for (let i = 0; i < points.length; i += 2) {
        out.push(clamp01(points[i] / iw), clamp01(points[i + 1] / ih));
    }
    return out;
}

export function clamp01(v: number): number {
    if (v < 0) return 0;
    if (v > 1) return 1;
    return v;
}

// Does a zone carry a drawable shape on the given floor plan?
export function isMappedOn(zone: models.Zone, floorPlanId: string): boolean {
    let m = zone && zone.mapData;
    return !!(m && m.floorPlanId === floorPlanId && m.shape && m.shape.type
        && m.shape.points && m.shape.points.length > 0);
}

// Axis-aligned bounding box of any shape, in normalized coords.
export function normalizedBBox(shape: models.ZoneShape): Box {
    let pts = shape.points || [];
    if (shape.type === 'rect') {
        return { x: pts[0], y: pts[1], width: pts[2], height: pts[3] };
    }
    if (shape.type === 'pin') {
        return { x: pts[0], y: pts[1], width: 0, height: 0 };
    }
    // polygon
    let xs: number[] = [], ys: number[] = [];
    for (let i = 0; i < pts.length; i += 2) { xs.push(pts[i]); ys.push(pts[i + 1]); }
    let minX = Math.min(...xs), minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}
